import { ArbitraryTypedObject } from '@portabletext/types'
import { PortableTextBodyElement } from './blockContent'
import { Translations } from './localization'
import { isNullOrWhiteSpace } from './string'

export interface BlockWithId {
  id?: string
}

export interface BlockAnchor {
  _key: string
  title: string
  anchor: string
}

/**
 * [block's _key]: id/anchor
 */
export type BlockIdMap = Record<string, string>

export type BlockTitleOverride = {
  _key: string
  title?: string
}

export const getBlockId = (
  block: PortableTextBodyElement,
  blockIdMap?: Record<string, string>
): string | undefined => blockIdMap?.[block?._key ?? '']

export const getBlockTitle = (
  block: ArbitraryTypedObject,
  translations: Translations
): string => {
  let title = ''

  if (block._type === 'businessLines') {
    title = translations.businessLines ?? ''
  } else if (
    (block._type === 'videoAutoplayPreview' || block._type === 'video') &&
    !block['displayTitle']
  ) {
    // no visible title
  } else if (block._type === 'annualReviews' && block['annualReviews']?.length < 1) {
    // not displayed
  } else {
    // check title/heading prop
    title =
      block.title ??
      block.heading ??
      block.mainTitle ??
      block.textTitle ??
      block.firstTitle ??
      block.secondTitle ??
      ''

    if (!title && block._rawBody) {
      // check for first heading in body
      const heading = block._rawBody.find(bodyblock => bodyblock.style === 'h1')
      if (heading) {
        title = heading.children
          .filter((child: ArbitraryTypedObject) => child._type === 'span')
          .map((child: ArbitraryTypedObject) => child.text)
          .join(' ')
      }
    }
  }

  return title
}

export const stringToId = (
  s: string,
  prefixForNonAlphaFirstCharacter?: string
): string => {
  const noSpace = s?.trim().replace(/[\W-]+/g, '-') ?? ''
  const id = noSpace.replace(/[^A-Za-z0-9-]/g, '').toLowerCase()
  if (prefixForNonAlphaFirstCharacter && !id.match(/^[a-z]+/)) {
    return `${prefixForNonAlphaFirstCharacter}${id}`
  } else {
    return id
  }
}

export const titleToUniqueId = (
  title: string,
  fallbackUniqueId: string,
  usedIds: Set<string>,
  useFallbackIdAsSuffix = false
): string => {
  const nonAlphaPrefix = 'section-'
  const idBase = useFallbackIdAsSuffix ? `${title}-${fallbackUniqueId}` : title
  const id = stringToId(idBase, nonAlphaPrefix)

  let uniqueId = id
  let num = 2

  while (usedIds.has(uniqueId) && num < 10) {
    uniqueId = `${id}-${num}`
    num++
  }

  if (usedIds.has(uniqueId)) {
    uniqueId = `${nonAlphaPrefix}${stringToId(fallbackUniqueId)}`
  }

  return uniqueId
}

export const getBlockTitlesAndIds = (
  contentBlocks: PortableTextBodyElement[],
  blockTitleOverrides: BlockTitleOverride[] | null | undefined,
  translations: Translations
): BlockAnchor[] => {
  const usedIds = new Set<string>()

  const blockTitleOverridesDict = !!blockTitleOverrides?.length
    ? blockTitleOverrides.reduce<Record<string, BlockTitleOverride>>(
        (acc, blockTitle) => {
          acc[blockTitle._key] = blockTitle
          return acc
        },
        {}
      )
    : undefined

  return contentBlocks?.reduce<BlockAnchor[]>((list, block) => {
    if (block._key) {
      const blockTitleOverride = blockTitleOverridesDict?.[block._key]

      if (!blockTitleOverridesDict || blockTitleOverride) {
        const title = blockTitleOverride?.title ?? getBlockTitle(block, translations)

        if (!isNullOrWhiteSpace(title)) {
          const id = titleToUniqueId(title, block._key, usedIds)

          usedIds.add(id)

          list.push({
            _key: block._key,
            title,
            anchor: id,
          })
        }
      }
    }

    return list
  }, [])
}

export const blockAnchorListToIdMap = (blockAnchorList: BlockAnchor[]): BlockIdMap =>
  blockAnchorList.reduce<BlockIdMap>((map, block) => {
    map[block._key] = block.anchor
    return map
  }, {})
